import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GenericFeatureFlagService } from '@rma/generic/ng/feature-flags/internal';
import { CurrentUser, PublicUserContextService } from '@rma/public/accounts/user';
import { DeviceIdentificationService } from '@rma/public/generic/util/device-identification';
import { PlatformService } from '@rma/public/generic/util/environment';
import { LDContextCommon, LDMultiKindContext } from 'launchdarkly-js-sdk-common';
import { distinctUntilChanged } from 'rxjs';
import { FeatureFlag } from '../domain/feature-flags.enum';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagUserIdentificationService {
  private readonly currentUserService = inject(PublicUserContextService);
  private readonly deviceIdService = inject(DeviceIdentificationService, {
    optional: true,
  });
  private readonly FeatureFlagService = inject(GenericFeatureFlagService<FeatureFlag>);
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);

  public initialise() {
    // Bots and crawlers each get their own LaunchDarkly context if we identify them,
    // which inflates MAU billing and fills the dashboard with single-request contexts.
    if (!this.platformService.isTrackable) {
      return;
    }
    this.currentUserService.currentUser$
      .pipe(
        distinctUntilChanged((a: CurrentUser | null, b: CurrentUser | null) => a?.userId === b?.userId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x: CurrentUser | null) => this.identifyUser(x));
  }

  private identifyUser(currentUser: CurrentUser | null) {
    const deviceId = this.deviceIdService?.getDeviceId();

    const userContext = this.getLdUserContext(currentUser);
    const deviceContext = this.getLdDeviceContext(deviceId, Boolean(userContext?.anonymous));

    const context: LDMultiKindContext = {
      kind: 'multi',
    };

    if (deviceContext) {
      context['device'] = deviceContext;
    }

    if (userContext) {
      context['user'] = userContext;
    }

    this.FeatureFlagService.identify(context);
  }

  private getLdDeviceContext(deviceId: string | undefined, isAnonymous: boolean): LDContextCommon | null {
    //If this works, we should extend the custom with browser information using bowser or ua parser
    return deviceId
      ? {
          key: deviceId,
          anonymous: isAnonymous,
          custom: {},
        }
      : null;
  }

  private getLdUserContext(currentUser: CurrentUser | null): LDContextCommon | null {
    return currentUser
      ? {
          key: currentUser.userId,
          name: currentUser.name,
          firstName: currentUser.firstname,
          lastName: currentUser.lastname,
          avatar: currentUser.profileImage,
          custom: {
            agencies: currentUser?.agencies?.map((agency) => agency.name) as string[],
          },
        }
      : { anonymous: true };
  }
}
